import React, { useState, useEffect } from 'react';
import { Modal } from './ui/Modal';
import ModalTabs from './ui/ModalTabs';
import CompactSelectPicker from './ui/CompactSelectPicker';
import { Mail, Link2, Hash, Plus, X, Copy, Check, Info, RefreshCw } from 'lucide-react';
import notificationsService from '../services/notificationsService';
import teamsService from '../services/teamsService';
import {
  canRegenerateInviteCode,
  inviteRegenerateCooldownMessage,
} from '../utils/inviteCodeCooldown';

const ROLE_OPTIONS = [
  { value: 'viewer', label: 'viewer' },
  { value: 'editor', label: 'editor' },
  { value: 'admin',  label: 'admin' },
];

const EXPIRES_OPTIONS = [
  { value: 'never', label: 'Never' },
  { value: '7d',    label: '7 days' },
  { value: '30d',   label: '30 days' },
  { value: '90d',   label: '90 days' },
];

const TABS = [
  { id: 'shareable', icon: Link2, label: 'Shareable link' },
  { id: 'email',     icon: Mail,  label: 'Email invite' },
  { id: 'code',      icon: Hash,  label: 'Code' },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

let _rowId = 0;
const nextId = () => ++_rowId;
const emptyRow = () => ({ id: nextId(), email: '', role: 'editor' });

const TeamAvatar = ({ team }) => {
  const bg = team?.color || 'var(--accent, #8869eb)';
  const letter = (team?.name || '?').charAt(0).toUpperCase();
  return (
    <div style={{
      width: 36, height: 36, borderRadius: 8, background: bg,
      flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 700, fontSize: 16, color: '#fff',
    }}>
      {letter}
    </div>
  );
};

const TeamInviteModal = ({ isOpen, onClose, team, showToast, onTeamUpdated }) => {
  const [activeTab, setActiveTab]         = useState('shareable');
  const [rows, setRows]                   = useState([emptyRow()]);
  const [message, setMessage]             = useState('');
  const [sending, setSending]             = useState(false);
  const [sendError, setSendError]         = useState('');

  const [linkRole, setLinkRole]           = useState('editor');
  const [requireApproval, setRequireApproval] = useState(true);
  const [linkExpires, setLinkExpires]     = useState('never');
  const [linkCopied, setLinkCopied]       = useState(false);

  const [codeCopied, setCodeCopied]       = useState(false);
  const [regenerating, setRegenerating]   = useState(false);
  const [inviteCode, setInviteCode]       = useState(team?.invite_code || '');
  const [regeneratedAt, setRegeneratedAt]   = useState(team?.invite_code_regenerated_at || null);

  const teamForCooldown = { invite_code_regenerated_at: regeneratedAt };
  const regenAllowed = canRegenerateInviteCode(teamForCooldown);
  const regenCooldownMessage = inviteRegenerateCooldownMessage(teamForCooldown);
  const inviteLink = inviteCode ? `https://apiboo.com/join/${inviteCode}` : '';

  useEffect(() => {
    if (!isOpen) return;
    setInviteCode(team?.invite_code || '');
    setRegeneratedAt(team?.invite_code_regenerated_at || null);
    setActiveTab('shareable');
    setRows([emptyRow()]);
    setMessage('');
    setSendError('');
    setLinkRole('editor');
    setRequireApproval(true);
    setLinkExpires('never');
    setLinkCopied(false);
    setCodeCopied(false);
  }, [isOpen, team?.invite_code, team?.invite_code_regenerated_at]);

  const updateRow = (id, field, val) =>
    setRows(prev => prev.map(r => r.id === id ? { ...r, [field]: val } : r));

  const removeRow = (id) =>
    setRows(prev => prev.length > 1 ? prev.filter(r => r.id !== id) : prev);

  const addRow = () => setRows(prev => [...prev, emptyRow()]);

  const validRows = rows.filter(r => EMAIL_RE.test(r.email.trim()));

  const handleSendEmails = async () => {
    if (validRows.length === 0) return;
    setSending(true);
    setSendError('');
    try {
      await Promise.all(
        validRows.map((r) =>
          notificationsService.sendTeamInvitation(team.id, r.email.trim(), r.role)
        )
      );
      showToast?.(`${validRows.length} invitation${validRows.length > 1 ? 's' : ''} sent`, 'success');
      onClose();
    } catch (err) {
      setSendError(err.message || 'Failed to send invitations');
    } finally {
      setSending(false);
    }
  };

  const copyLink = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      showToast?.('Failed to copy', 'error');
    }
  };

  const copyCode = async () => {
    if (!inviteCode) return;
    try {
      await navigator.clipboard.writeText(inviteCode);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch {
      showToast?.('Failed to copy', 'error');
    }
  };

  const handleRegenerate = async () => {
    if (!team?.id) return;
    if (!regenAllowed) {
      showToast?.(regenCooldownMessage, 'error');
      return;
    }
    setRegenerating(true);
    try {
      const res = await teamsService.regenerateInviteCode(team.id);
      if (res?.invite_code) setInviteCode(res.invite_code);
      const at = res?.invite_code_regenerated_at || new Date().toISOString();
      setRegeneratedAt(at);
      onTeamUpdated?.({
        invite_code: res?.invite_code,
        invite_code_regenerated_at: at,
      });
      showToast?.('Invite code regenerated', 'success');
    } catch (err) {
      showToast?.(err.message || 'Failed to regenerate code', 'error');
    } finally {
      setRegenerating(false);
    }
  };

  const handleClose = () => {
    setRows([emptyRow()]);
    setMessage('');
    setSendError('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={team?.name || 'Team'}
      subtitle="Send invites by email or share a join link with role"
      customIcon={<TeamAvatar team={team} />}
      size="lg"
    >
      <ModalTabs tabs={TABS} activeTab={activeTab} onTabChange={setActiveTab} />

      <div>

        {/* ── Email invite tab ───────────────────────────────── */}
        {activeTab === 'email' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {sendError && (
              <div style={{ borderRadius: '0.5rem', border: '1px solid #fecaca', background: '#fef2f2', padding: '0.5rem 0.75rem', fontSize: '0.8125rem', color: '#b91c1c' }}>
                {sendError}
              </div>
            )}

            <div>
              <p style={{ marginBottom: '0.5rem', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280' }}>
                People to invite{rows.length > 0 ? ` ${rows.length}` : ''}
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {rows.map(row => (
                  <div key={row.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <Mail size={14} style={{ position: 'absolute', left: '0.625rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
                      <input
                        type="email"
                        value={row.email}
                        onChange={e => updateRow(row.id, 'email', e.target.value)}
                        placeholder="name@email.com"
                        className="modal-invite-input modal-input-no-focus-outline"
                      />
                    </div>
                    <div className="modal-picker" style={{ minWidth: 108 }}>
                      <CompactSelectPicker
                        value={row.role}
                        onChange={val => updateRow(row.id, 'role', val)}
                        options={ROLE_OPTIONS}
                        aria-label="Role"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removeRow(row.id)}
                      disabled={rows.length === 1}
                      className="modal-row-remove"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={addRow}
                className="modal-btn-ghost"
                style={{ marginTop: '0.5rem' }}
              >
                <Plus size={13} />
                Add another
              </button>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.375rem', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280' }}>
                Personal message <span style={{ fontWeight: 400, textTransform: 'none' }}>(optional)</span>
              </label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Hey — joining our REST API workspace, let me know if you need anything."
                rows={3}
                className="modal-form-textarea"
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem', paddingTop: '0.25rem' }}>
              <p className="modal-footer-info">
                <Info size={12} />
                Invited members get an email + see this team in their list.
              </p>
              <button
                type="button"
                onClick={handleSendEmails}
                disabled={sending || validRows.length === 0}
                className="rounded-lg bg-blue-600 text-white modern-button"
                style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 500, border: 'none', cursor: 'pointer', opacity: (sending || validRows.length === 0) ? 0.5 : 1 }}
              >
                {sending ? 'Sending…' : `Send ${validRows.length || ''} invite${validRows.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        )}

        {/* ── Shareable link tab ────────────────────────────── */}
        {activeTab === 'shareable' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <p style={{ marginBottom: '0.5rem', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280' }}>
                Invite link
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <Link2 size={13} style={{ position: 'absolute', left: '0.625rem', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
                  <input
                    type="text"
                    readOnly
                    value={inviteLink || 'Loading…'}
                    className="modal-invite-input modal-input-no-focus-outline"
                    style={{ paddingLeft: '2.125rem', cursor: 'default' }}
                  />
                </div>
                <button
                  type="button"
                  onClick={copyLink}
                  disabled={!inviteLink}
                  className="bg-blue-600 rounded-lg"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', padding: '0.5625rem 0.875rem', fontSize: '0.8125rem', fontWeight: 500, color: '#fff', border: 'none', cursor: inviteLink ? 'pointer' : 'not-allowed', opacity: inviteLink ? 1 : 0.5, whiteSpace: 'nowrap', borderRadius: '0.625rem' }}
                >
                  {linkCopied ? <Check size={14} /> : <Copy size={14} />}
                  {linkCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <p style={{ marginTop: '0.375rem', fontSize: '0.75rem', color: '#9ca3af' }}>
                Anyone with this link can request to join with the role below.
              </p>
            </div>

            <div>
              <p style={{ marginBottom: '0.5rem', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280' }}>
                Default role for link
              </p>
              <div className="modal-picker" style={{ minWidth: 120 }}>
                <CompactSelectPicker
                  value={linkRole}
                  onChange={setLinkRole}
                  options={ROLE_OPTIONS}
                  aria-label="Default role"
                />
              </div>
            </div>

            <div>
              <div className="settings-row">
                <div>
                  <p className="settings-row-label">Require approval</p>
                  <p className="settings-row-desc">New members need owner approval before joining</p>
                </div>
                <button
                  type="button"
                  className={`settings-toggle${requireApproval ? ' settings-toggle--on' : ''}`}
                  onClick={() => setRequireApproval(v => !v)}
                  aria-checked={requireApproval}
                  role="switch"
                >
                  <span className="settings-toggle-thumb" />
                </button>
              </div>

              <div className="settings-row">
                <div>
                  <p className="settings-row-label">Expires</p>
                  <p className="settings-row-desc">Auto-rotate the code on a schedule</p>
                </div>
                <div className="modal-picker" style={{ minWidth: 108 }}>
                  <CompactSelectPicker
                    value={linkExpires}
                    onChange={setLinkExpires}
                    options={EXPIRES_OPTIONS}
                    aria-label="Expires"
                  />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem', paddingTop: '0.25rem' }}>
              <p className="modal-footer-info">
                <Info size={12} />
                Invited members get an email + see this team in their list.
              </p>
              <button
                type="button"
                onClick={handleClose}
                className="bg-blue-600 rounded-lg"
                style={{ width: '100%', padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 500, color: '#fff', border: 'none', cursor: 'pointer', borderRadius: '0.5rem' }}
              >
                Done
              </button>
            </div>
          </div>
        )}

        {/* ── Code tab ──────────────────────────────────────── */}
        {activeTab === 'code' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <p style={{ marginBottom: '0.5rem', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#6b7280' }}>
                Invite code
              </p>
              <div className="modal-invite-code-box">
                <span className="modal-invite-code">
                  {inviteCode || '—'}
                </span>
                <button
                  type="button"
                  onClick={copyCode}
                  disabled={!inviteCode}
                  className="bg-blue-600"
                  style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', padding: '0.5rem 0.875rem', fontSize: '0.8125rem', fontWeight: 500, color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: inviteCode ? 'pointer' : 'not-allowed', opacity: inviteCode ? 1 : 0.5 }}
                >
                  {codeCopied ? <Check size={13} /> : <Copy size={13} />}
                  {codeCopied ? 'Copied!' : 'Copy code'}
                </button>
              </div>
              <p style={{ marginTop: '0.375rem', fontSize: '0.75rem', color: '#9ca3af' }}>
                Share this code — people can enter it at apiboo.com/join
              </p>
            </div>

            <div className="modal-regenerate-box">
              <span className="modal-regenerate-text">
                {regenAllowed
                  ? 'Regenerate this code if it was compromised. Limited to once every 24 hours.'
                  : regenCooldownMessage}
              </span>
              <button
                type="button"
                onClick={handleRegenerate}
                disabled={regenerating || !regenAllowed}
                style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.375rem 0.75rem', fontSize: '0.8125rem', fontWeight: 500, color: '#374151', background: 'transparent', border: '1px solid #e5e7eb', borderRadius: '0.5rem', cursor: (regenerating || !regenAllowed) ? 'not-allowed' : 'pointer', opacity: (regenerating || !regenAllowed) ? 0.6 : 1, fontFamily: 'inherit' }}
              >
                <RefreshCw size={12} className={regenerating ? 'animate-spin' : ''} />
                Regenerate
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem', paddingTop: '0.25rem' }}>
              <p className="modal-footer-info">
                <Info size={12} />
                Invited members get an email + see this team in their list.
              </p>
              <button
                type="button"
                onClick={handleClose}
                className="bg-blue-600"
                style={{ width: '100%', padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 500, color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer' }}
              >
                Done
              </button>
            </div>
          </div>
        )}

      </div>
    </Modal>
  );
};

export default TeamInviteModal;
